import { Briefcase, Flag, Lock, Unlock, Check, X as XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MangaSFX } from '../../../experience/MangaSFX';
import { GoldCorners } from '../WizardDividers';

type TravellerType = 'casual' | 'enthusiast';

interface IdentityStepProps {
  selectedType: TravellerType;
  onSelect: (t: TravellerType) => void;
  onTourFirst?: () => void;
  isManga: boolean;
  isUkiyo: boolean;
}

interface CardConfig { type: TravellerType; icon: typeof Briefcase; labelKey: string; labelJp: string; descKey: string }
const cardConfigs: CardConfig[] = [
  {
    type: 'casual',
    icon: Briefcase,
    labelKey: 'identityStep.casualLabel',
    labelJp: '\u65C5\u884C\u8005',
    descKey: 'identityStep.casualDesc',
  },
  {
    type: 'enthusiast',
    icon: Flag,
    labelKey: 'identityStep.enthusiastLabel',
    labelJp: '\u611B\u597D\u5BB6',
    descKey: 'identityStep.enthusiastDesc',
  },
];

interface FeatureRow {
  labelKey: string;
  casual: boolean;
  enthusiast: boolean;
}

const features: FeatureRow[] = [
  { labelKey: 'identityStep.feature.planner', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.dragDrop', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.transit', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.library', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.views', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.pdf', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.canvas', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.browse', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.phrasebook', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.icCard', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.seasonalEvents', casual: true, enthusiast: true },
  { labelKey: 'identityStep.feature.stamps', casual: false, enthusiast: true },
  { labelKey: 'identityStep.feature.streaks', casual: false, enthusiast: true },
  { labelKey: 'identityStep.feature.shop', casual: false, enthusiast: true },
  { labelKey: 'identityStep.feature.xp', casual: false, enthusiast: true },
  { labelKey: 'identityStep.feature.voting', casual: false, enthusiast: true },
  { labelKey: 'identityStep.feature.weekly', casual: false, enthusiast: true },
  { labelKey: 'identityStep.feature.leaderboard', casual: false, enthusiast: true },
];

export function IdentityStep({ selectedType, onSelect, onTourFirst, isManga, isUkiyo }: IdentityStepProps) {
  const { t } = useTranslation('pages');
  return (
    <div>
      {onTourFirst && (
        <div
          className={`mb-5 p-4 text-center cursor-pointer transition-all ${
            isManga
              ? ''
              : isUkiyo
              ? ''
              : 'rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/30'
          }`}
          style={
            isManga ? {
              border: '2px solid #0a0a0a', background: '#f5f0e8',
              boxShadow: '4px 4px 0 rgba(0,0,0,0.15)', padding: 16,
            } : isUkiyo ? {
              border: '1px solid rgba(27,79,138,0.3)', background: '#EDE4CC',
              borderRadius: 2, padding: 16, position: 'relative' as const,
            } : undefined
          }
          onClick={onTourFirst}
        >
          {isUkiyo && <GoldCorners />}
          <p className={`font-semibold text-sm mb-1 ${
            isManga ? 'text-[#0a0a0a]' : isUkiyo ? 'text-[#1B5C8A]' : 'text-primary-700 dark:text-primary-300'
          }`}>
            {t('identityStep.tourFirstTitle')}
          </p>
          <p className={`text-xs ${
            isManga ? 'text-[#555]' : isUkiyo ? 'text-[#1A1410]/70' : 'text-gray-500 dark:text-gray-400'
          }`}>
            {t('identityStep.tourFirstSubtitle')}
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cardConfigs.map(card => {
          const selected = selectedType === card.type;
          const Icon = card.icon;

          const mangaStyle = isManga ? {
            border: selected ? '3px solid #0a0a0a' : '2px solid #0a0a0a',
            borderRadius: 0,
            boxShadow: '4px 4px 0 rgba(0,0,0,0.20)',
          } : undefined;

          const ukiyoStyle = isUkiyo ? {
            background: selected ? '#1B5C8A' : '#F5EFE0',
            border: selected ? '2px solid #0D3A6B' : '1px solid rgba(27,79,138,0.28)',
            boxShadow: selected ? '2px 3px 0 rgba(10,40,90,0.15)' : '2px 3px 0 rgba(10,40,90,0.08)',
            borderRadius: 2,
            position: 'relative' as const,
          } : undefined;

          const baseClasses = !isManga && !isUkiyo
            ? `rounded-2xl p-5 cursor-pointer transition-all duration-200 relative ${
                selected
                  ? 'bg-primary-700 text-white'
                  : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'
              }`
            : isManga
              ? `p-5 cursor-pointer transition-all duration-200 relative ${
                  selected ? 'bg-[#0a0a0a] text-white' : 'bg-white text-[#0a0a0a]'
                }`
              : 'p-5 cursor-pointer transition-all duration-200 relative';

          return (
            <button
              key={card.type}
              type="button"
              onClick={() => onSelect(card.type)}
              className={baseClasses}
              style={mangaStyle || ukiyoStyle || undefined}
            >
              {isUkiyo && <GoldCorners />}
              <div className="flex items-start gap-3">
                <Icon size={20} className={`mt-0.5 shrink-0 ${
                  isUkiyo ? (selected ? 'text-white' : 'text-[#1B5C8A]') : ''
                }`} />
                <div className="text-left">
                  <p className={`font-semibold text-base ${
                    isUkiyo && selected ? 'text-white' : isUkiyo ? 'text-[#1A1410]' : ''
                  }`}>
                    {t(card.labelKey)}
                  </p>
                  <p className={`text-xs mt-0.5 ${
                    isUkiyo
                      ? selected ? 'text-white/70' : 'text-[#1B5C8A]'
                      : selected && !isManga ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'
                  }`} style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    {card.labelJp}
                  </p>
                  <p className={`text-sm mt-2 leading-relaxed ${
                    isUkiyo
                      ? selected ? 'text-white/90' : 'text-[#1A1410]/80'
                      : selected && !isManga ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {t(card.descKey)}
                  </p>
                </div>
              </div>
              {isManga && selected && (
                <div className="absolute top-2 right-2">
                  <MangaSFX text={t('identityStep.selectedStamp')} variant="stamp" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Feature comparison grid */}
      <div style={{
        marginTop: 16, borderRadius: isManga ? 0 : isUkiyo ? 2 : 12,
        border: isManga ? '2px solid #0a0a0a' : isUkiyo ? '1px solid rgba(27,79,138,0.25)' : '1px solid rgba(0,0,0,0.08)',
        overflow: 'hidden',
        fontSize: 12,
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 32px 32px',
          background: isManga ? '#0a0a0a' : isUkiyo ? '#0D3A6B' : '#1e293b',
          padding: '6px 12px', gap: 4,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 700, fontSize: 9, letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#fff',
        }}>
          <span>{t('onboarding.feature')}</span>
          <span style={{ textAlign: 'center' }}>C</span>
          <span style={{ textAlign: 'center' }}>E</span>
        </div>
        {features.map((f, i) => {
          const included = selectedType === 'casual' ? f.casual : f.enthusiast;
          return (
            <div key={f.labelKey} style={{
              display: 'grid', gridTemplateColumns: '1fr 32px 32px',
              padding: '5px 12px', gap: 4,
              background: isManga
                ? (i % 2 === 0 ? '#fff' : '#f5f0e8')
                : isUkiyo
                  ? (i % 2 === 0 ? '#F5EFE0' : '#EDE4CC')
                  : (i % 2 === 0 ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,0.02)'),
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 11, lineHeight: 1.3,
              color: isManga ? '#0a0a0a' : isUkiyo ? '#1A1410' : undefined,
            }}
            className={!isManga && !isUkiyo ? (i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50') : ''}
            >
              <span className={!isManga && !isUkiyo ? 'text-gray-700 dark:text-gray-300' : ''}>
                {t(f.labelKey)}
              </span>
              <span style={{ textAlign: 'center' }}>
                {f.casual
                  ? <Check size={13} style={{ color: '#22c55e', display: 'inline' }} />
                  : <XIcon size={13} style={{ color: '#94a3b8', display: 'inline', opacity: 0.4 }} />
                }
              </span>
              <span style={{ textAlign: 'center' }}>
                {f.enthusiast
                  ? <Check size={13} style={{ color: '#22c55e', display: 'inline' }} />
                  : <XIcon size={13} style={{ color: '#94a3b8', display: 'inline', opacity: 0.4 }} />
                }
              </span>
            </div>
          );
        })}
      </div>

      {isUkiyo ? (
        <div style={{
          border: '1px solid rgba(27,79,138,0.25)', background: '#EDE4CC',
          padding: '9px 12px', position: 'relative', marginTop: 12,
        }}>
          <div style={{ position: 'absolute', top: 4, left: 4, width: 8, height: 8, borderTop: '1.5px solid #B8860B', borderLeft: '1.5px solid #B8860B' }} />
          <div style={{ position: 'absolute', bottom: 4, right: 4, width: 8, height: 8, borderBottom: '1.5px solid #B8860B', borderRight: '1.5px solid #B8860B' }} />
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: '#1B5C8A', lineHeight: 1.5, margin: 0, paddingLeft: 4 }}>
            {selectedType === 'casual'
              ? t('identityStep.casualInfo')
              : t('identityStep.enthusiastInfo')}
          </p>
        </div>
      ) : (
        <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 text-sm ${
          selectedType === 'casual'
            ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-800'
            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
        }`}>
          {selectedType === 'casual'
            ? <Lock size={14} className="mt-0.5 shrink-0" />
            : <Unlock size={14} className="mt-0.5 shrink-0" />
          }
          <p>
            {selectedType === 'casual'
              ? t('identityStep.casualInfo')
              : t('identityStep.enthusiastInfo')}
          </p>
        </div>
      )}
    </div>
  );
}
